import {
  ContentLocaleEnum,
  getMe,
  LocaleEnum,
  UserTypeEnum,
  type UserUnion,
  type User,
  type Pupil,
  type Visitor,
  type Group,
} from "@/services/internalApiV1";
import {
  type InternationalFilter,
  internationalFilterForLocales,
} from "@/utils/internationalFilter";
import { formatLocalesFilter } from "@/utils/localesFilter";
import { isAxiosError } from "axios";
import { defineStore } from "pinia";

export type RefreshResult = "ok" | "expired" | "error";

type UserStoreType = {
  user?: UserUnion;
  accessToken?: string;
  publicToken?: string;
  visitorToken?: string;
  itemPassword?: string;
  itemPasswordRequired: boolean;
  itemPasswordErrorCount: number;
  projectId?: string;
};

export const isPupil = (user?: unknown): user is Pupil => {
  return (user as UserUnion)?.type === UserTypeEnum.Pupil;
};

export const isUser = (user?: unknown): user is User => {
  return (user as UserUnion)?.type === UserTypeEnum.User;
};

export const isVisitor = (user?: unknown): user is Visitor => {
  return (user as UserUnion)?.type === UserTypeEnum.Visitor;
};

export const isInternational = (user?: UserUnion): boolean => {
  return isUser(user) && !!user.international;
};

// The backend decides the default content languages (ContentLocales: the saved
// choice, else the international flag plus the account locale). Courses and
// materials start from them. Pupils and visitors don't get the field and keep
// German.
export const defaultContentLocales = (
  user?: UserUnion
): ContentLocaleEnum[] => {
  return user && "defaultContentLocales" in user
    ? user.defaultContentLocales
    : [ContentLocaleEnum.DE];
};

// The `international` filter for assistants, mini tools and templates: the
// content languages mapped onto their German/English flag.
export const defaultInternationalFilter = (
  user?: UserUnion
): InternationalFilter =>
  internationalFilterForLocales(defaultContentLocales(user));

// The gallery's language filter starts from the default content languages,
// minus the ones materials don't have (es). Spanish-only content languages
// fall back to English, as an empty filter would mean every language.
export const defaultMaterialLocales = (user?: UserUnion): LocaleEnum[] => {
  const materialLocales: string[] = Object.values(LocaleEnum);
  const locales = defaultContentLocales(user).filter(
    (locale): locale is LocaleEnum => materialLocales.includes(locale)
  );
  return locales.length > 0 ? locales : [LocaleEnum.EN];
};

// The assistants' and mini tools' language filter starts from the content
// languages minus es (docs/exec-plans/ai-content-locale.md, D1).
export const defaultLocalesFilter = (user?: UserUnion): string =>
  formatLocalesFilter(defaultMaterialLocales(user));

export const isToTeachLinked = (user?: UserUnion): boolean => {
  return isUser(user) && !!user.toTeachSsoEnabled;
};

export const useUserStore = defineStore("user", {
  state: (): UserStoreType => {
    return {
      user: undefined,
      accessToken: undefined,
      publicToken: undefined,
      visitorToken: undefined,
      itemPassword: undefined,
      itemPasswordRequired: false,
      itemPasswordErrorCount: 0,
      projectId: undefined,
    };
  },
  getters: {
    isAuthenticated(state) {
      return isUser(state.user) || isPupil(state.user);
    },
    // The internal API rejects every session-less request with 401, public
    // items included — a plain visitor session is the minimum. Authenticated
    // users are seeded in `main.ts`; anonymous ones only get a session once
    // `<WithVisitor>` has created a visitor and refetched the user. Queries on
    // public pages must wait for that, otherwise they resolve to an error view
    // before the visitor exists (#11157).
    //
    // `user` alone is not the signal: the server seeds a `LoggedOutUser`
    // placeholder into `data-user`, which carries no session at all.
    hasSession(state) {
      return isUser(state.user) || isPupil(state.user) || isVisitor(state.user);
    },
    isAdmin(state) {
      return isUser(state.user) && state.user.admin;
    },
    isVisitor(state) {
      return isVisitor(state.user);
    },
    isLoggedIn(state) {
      return !isVisitor(state.user);
    },
    isIncognito(state) {
      if (!state.user) return false;

      if (isUser(state.user) || isPupil(state.user) || isVisitor(state.user)) {
        return !!(state.user as User | Pupil | Visitor).incognito;
      }

      return false;
    },
    isPupil(state) {
      return isPupil(state.user);
    },
    isUser(state) {
      return isUser(state.user);
    },
    firstname(state) {
      if (isUser(state.user)) {
        return state.user?.firstname;
      }
    },
    hasUnreadPosts(state) {
      return isUser(state.user) ? state.user?.hasUnreadPosts : undefined;
    },
    hasActiveWorkshops(state) {
      return isUser(state.user) ? state.user?.hasActiveWorkshops : undefined;
    },
    isPlatformReviewer(state) {
      return isUser(state.user) ? state.user?.reviewer : undefined;
    },
    additionalQualificationGroup(state) {
      return isUser(state.user)
        ? state.user?.additionalQualificationGroup
        : undefined;
    },
    additionalQualificationUrl() {
      if (this.additionalQualificationGroup?.additionalQualification) {
        return (this.additionalQualificationGroup as unknown as Group).url;
      }

      return undefined;
    },
    disabledTools(state) {
      return isUser(state.user) ? state.user?.disabledTools : [];
    },
    disabledToolsFeatures(state) {
      return isUser(state.user) ? state.user?.disabledToolsFeatures : [];
    },
    isDisabledTool(state) {
      return (tool: string) => {
        return isUser(state.user)
          ? state.user?.disabledTools?.includes(tool)
          : false;
      };
    },
    // Opt-in tools (ai_feedback, pupil_feedback) are hidden unless one of the
    // user's schools unlocked them in the backoffice. Deliberately positive:
    // `isDisabledTool` is false for anyone who is not a signed-in user, so a
    // negated check would expose them to logged-out visitors.
    isOptInToolUnlocked(state) {
      return (tool: string) => {
        return isUser(state.user) && !state.user?.disabledTools?.includes(tool);
      };
    },
    isDisabledToolsFeature(state) {
      return (feature: string) => {
        return (
          isUser(state.user) &&
          state.user?.disabledToolsFeatures?.includes(feature)
        );
      };
    },
    name(state) {
      return state.user?.name ?? "Anonymous";
    },
    publicName(state) {
      if (isUser(state.user)) {
        return state.user?.publicName ?? "Anonymous";
      } else {
        return state.user?.name ?? "Anonymous";
      }
    },
    shortName(): string {
      if (!this.name) {
        return "";
      }

      return this.name.substring(0, 30);
    },
    isFeatureEnabled(state) {
      return (feature: string) => {
        return state.user?.enabledFeatures?.includes(feature);
      };
    },
    hasToolsAccess(state): boolean {
      if (!isUser(state.user)) return false;
      return (
        !!state.user.admin ||
        !!state.user.licenses?.hasToolsPro ||
        !!state.user.licenses?.hasToolsGroup
      );
    },
    // The school ("Gruppen-") licence on its own, trial included. Narrower than
    // `hasToolsAccess`, which a Pro single licence also satisfies: the
    // Gemeinsame Ablage is bound to the school, so this is the flag it gates on.
    hasSchoolLicense(state): boolean {
      return isUser(state.user) && !!state.user.licenses?.hasToolsGroup;
    },
    // The individual ("Einzel-") Pro licence on its own, paid or free. The
    // counterpart of `hasSchoolLicense`, and deliberately just as narrow: a
    // teacher can hold both, and which one wins is the caller's decision, not
    // this getter's. `hasToolsPro` is the b2c side of `hasToolsAccess`
    // (`licenses_context.rb` `tools_pro_license?` / `tools_pro_free_license?`).
    hasIndividualLicense(state): boolean {
      return isUser(state.user) && !!state.user.licenses?.hasToolsPro;
    },
  },
  actions: {
    // Single source of truth for loading the current user from the server and
    // writing it into the store (incl. visitor token and pupil-only projectId
    // cleanup). A `401` resets the store and is reported as "expired" rather
    // than throwing, so session handling stays in one place; callers only
    // decide whether to redirect.
    async refresh(): Promise<RefreshResult> {
      try {
        const user = await getMe();
        this.user = user;
        this.visitorToken = isVisitor(user) ? user.toolsUserId : undefined;
        if (!isPupil(user)) {
          this.projectId = undefined;
        }
        return "ok";
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          // Session was rejected server-side: clear it so no stale identity or
          // permissions linger. Callers decide whether to redirect.
          this.reset();
          return "expired";
        }
        console.error(error);
        return "error";
      }
    },
    reset() {
      this.user = undefined;
      this.accessToken = undefined;
      this.publicToken = undefined;
      this.itemPassword = undefined;
      this.itemPasswordRequired = false;
      this.itemPasswordErrorCount = 0;
      this.projectId = undefined;
    },
    clearPassword() {
      this.itemPassword = undefined;
    },
    saveProjectId(projectId: string) {
      if (isPupil(this.user)) {
        this.projectId = projectId;
      }
    },
    clearProjectId() {
      this.projectId = undefined;
    },
  },
  persist: {
    pick: ["projectId"],
  },
});
